// src/components/dashboard/ClinicalDocHistory.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const ClinicalDocHistory = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme")
      );
    };
    checkTheme();
    const obs = new MutationObserver(checkTheme);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Filter Form States (Exact to Screenshot)
  const [clientName, setClientName] = useState("");
  const [staff, setStaff] = useState("");
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [formCodes, setFormCodes] = useState("");
  const [program, setProgram] = useState("All");

  // Pagination & Exact Dataset States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [documentRows] = useState([
    { id: 1, number: "NEZ-2401", name: "Collateral Note", lastCreated: "7/30/2026", created: 5 },
    { id: 2, number: "NEZ-2300", name: "ARMHS Crisis Plan", lastCreated: "7/24/2026", created: 15 },
    { id: 3, number: "NEZ-2400", name: "ADDA Supervision/Collaboration Note", lastCreated: "7/29/2026", created: 2 },
    { id: 4, number: "NEZ-8000", name: "MHIS Client Form", lastCreated: "7/14/2026", created: 1 },
    { id: 5, number: "NEZ-2003", name: "PHQ9", lastCreated: "7/24/2026", created: 1 },
    { id: 6, number: "NEZ-9004", name: "OBGYN", lastCreated: "7/28/2026", created: 6 },
    { id: 7, number: "NEZ-9006", name: "Client Session Verification", lastCreated: "7/17/2026", created: 1 },
    { id: 8, number: "NEZ-9064", name: "Postpartum Visit Updated", lastCreated: "7/17/2026", created: 1 },
  ]);

  // Design System Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setClientName("");
    setStaff("");
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setFormCodes("");
    setProgram("All");
  };

  const filteredRows = documentRows.filter((r) => {
    return formCodes === "" || r.number.toLowerCase().includes(formCodes.toLowerCase()) || r.name.toLowerCase().includes(formCodes.toLowerCase());
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DescriptionRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Clinical Document Usage Report
            </Typography>
            <Chip
              label="Forms & Usage"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Track clinical document utilization and completion
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Documents Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Documents
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Client Name */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Client Name</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Search and select patients...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Clients</MenuItem>
              <MenuItem value="Eleanor Vance">Eleanor Vance</MenuItem>
              <MenuItem value="Charlotte Bennett">Charlotte Bennett</MenuItem>
              <MenuItem value="Marcus Aurelius Vance">Marcus Aurelius Vance</MenuItem>
            </Select>
          </Box>

          {/* Staff */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Staff</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select staff...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Staff</MenuItem>
              <MenuItem value="Dr. Usman Rehman">Dr. Usman Rehman</MenuItem>
              <MenuItem value="Elena Rostova, RN">Elena Rostova, RN</MenuItem>
              <MenuItem value="TruEvv">TruEvv</MenuItem>
            </Select>
          </Box>

          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Quick Range</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select date range...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </Box>

          {/* Date of Session (from to) */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Date of Session</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>

          {/* Form Codes */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Form Codes</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={formCodes}
              onChange={(e) => setFormCodes(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select Forms</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="">All Forms</MenuItem>
              <MenuItem value="NEZ-2401">NEZ-2401 - Collateral Note</MenuItem>
              <MenuItem value="NEZ-2300">NEZ-2300 - ARMHS Crisis Plan</MenuItem>
              <MenuItem value="NEZ-2400">NEZ-2400 - ADDA Supervision</MenuItem>
              <MenuItem value="NEZ-8000">NEZ-8000 - MHIS Client Form</MenuItem>
            </Select>
          </Box>

          {/* Program */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Program</Typography>
            <Box sx={{ position: "relative" }}>
              <Select
                fullWidth
                size="small"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
                endAdornment={
                  program !== "All" && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setProgram("All"); }} sx={{ mr: 2, color: secondaryText, p: 0.3 }}>
                      <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="ARMHS">ARMHS</MenuItem>
                <MenuItem value="Doula Services">Doula Services</MenuItem>
                <MenuItem value="RN Services">RN Services</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        {/* Buttons: Search & Clear */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            sx={{ height: "40px", backgroundColor: tealMain, color: "#FFFFFF", fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 3, textTransform: "none", boxShadow: "none", "&:hover": { backgroundColor: tealButton } }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon />}
            sx={{ height: "40px", borderColor: tealButton, color: tealButton, fontWeight: 700, fontSize: "0.84rem", borderRadius: "8px", px: 2.8, textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table Container */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "350px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { color: primaryText, fontWeight: 800, fontSize: "0.86rem", backgroundColor: isDark ? "#162238" : "#F8FAFC", py: 2, px: 3 } }}>
                <TableCell>Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Last Created</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 10, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryText, fontSize: "0.9rem", fontWeight: 600 }}>No rows</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" } }}>
                    <TableCell sx={{ fontWeight: 600, color: primaryText, fontSize: "0.85rem" }}>{row.number}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.85rem" }}>{row.name}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.85rem" }}>{row.lastCreated}</TableCell>
                    <TableCell sx={{ color: primaryText, fontSize: "0.85rem" }}>{row.created}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default ClinicalDocHistory;